export type CustomerVerifyResult<TUser> =
  | { success: true; exp?: number | null; user?: TUser | null }
  | { success: false; authFailure: boolean }

export type CustomerMeResult<TUser> =
  | { success: true; exp?: number | null; user: TUser }
  | { success: false; authFailure: boolean }

type CustomerVerifyPayload<TUser> = {
  success?: unknown
  exp?: unknown
  user?: TUser | null
  authFailure?: unknown
  transient?: unknown
}

function hasJsonContentType(response: Response): boolean {
  const contentType = response.headers.get('content-type') ?? ''
  return /^application\/(?:[a-z0-9.+-]+\+)?json(?:\s*;|$)/i.test(contentType)
}

async function readVerifyPayload<TUser>(
  response: Response
): Promise<CustomerVerifyPayload<TUser> | null> {
  if (!hasJsonContentType(response)) return null

  const payload = (await response.json().catch(() => null)) as unknown
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }

  return payload as CustomerVerifyPayload<TUser>
}

export async function classifyCustomerVerifyResponse<TUser>(
  response: Response
): Promise<CustomerVerifyResult<TUser>> {
  const payload = await readVerifyPayload<TUser>(response)

  // An edge challenge or intermediary error may use an auth-looking HTTP status
  // while returning HTML. Never destroy a customer session without a valid API
  // response confirming that authentication failed.
  if (!payload) {
    return { success: false, authFailure: false }
  }

  if (response.ok) {
    if (payload.success === true) {
      return {
        success: true,
        exp: typeof payload.exp === 'number' ? payload.exp : null,
        user: payload.user ?? null,
      }
    }

    if (payload.authFailure === false || payload.transient === true) {
      return { success: false, authFailure: false }
    }

    // The existing verify endpoint returns { success: false } with HTTP 200 for
    // a missing or invalid customer session.
    if (payload.success === false) {
      return { success: false, authFailure: true }
    }

    return { success: false, authFailure: false }
  }

  return {
    success: false,
    authFailure: payload.authFailure === true,
  }
}

export async function classifyCustomerMeResponse<TUser>(
  response: Response
): Promise<CustomerMeResult<TUser>> {
  const payload = await readVerifyPayload<TUser>(response)

  if (
    response.ok &&
    payload?.success === true &&
    payload.user !== null &&
    payload.user !== undefined
  ) {
    return {
      success: true,
      exp: typeof payload.exp === 'number' ? payload.exp : null,
      user: payload.user,
    }
  }

  return {
    success: false,
    authFailure: payload?.authFailure === true,
  }
}
